import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
const BRIDGE_PORT = 11235;
const BRIDGE_BASE = `http://localhost:${BRIDGE_PORT}`;
const BRIDGE_WS = `ws://localhost:${BRIDGE_PORT}/ws`;
const HEALTH_CHECK_INTERVAL = 10_000;
// `localhost` resolves to ::1 before 127.0.0.1 on Windows, and the bridge
// listens on IPv4 only. Where the ::1 attempt is dropped rather than refused,
// the fallback to IPv4 is slow -- one reporter measured 2.19s against a 2s
// budget, so a running bridge was reported as missing. Allow for the fallback.
const HEALTH_CHECK_TIMEOUT = 5000;
export interface ScannerInfo {
  id: string;
  name: string;
  scannerType: "flatbed" | "feeder" | "unknown";
}
export interface ScanOptions {
  scannerId: string;
  dpi?: number;
  colorMode?: "color" | "grayscale" | "blackwhite";
  format?: "pdf" | "png" | "jpeg";
}
interface PageData {
  dataUrl: string;
  width: number;
  height: number;
}
type ScanState = "idle" | "connecting" | "scanning" | "complete" | "error";
interface ScannerBridgeState {
  isAvailable: boolean;
  scanners: ScannerInfo[];
  scanState: ScanState;
  scannedPages: PageData[];
  currentPage: number;
  error: string | null;
}
export function useScannerBridge() {
  const $ = _c(24);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      isAvailable: false,
      scanners: t0,
      scanState: "idle",
      scannedPages: [],
      currentPage: 0,
      error: null
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, setState] = useState(t1);
  const wsRef = useRef(null);
  const healthTimerRef = useRef(undefined);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async () => {
      try {
        const res = await fetch(`${BRIDGE_BASE}/health`, {
          signal: AbortSignal.timeout(HEALTH_CHECK_TIMEOUT)
        });
        if (res.ok) {
          setState(_temp);
          return true;
        }
      } catch {
        setState(_temp2);
      }
      return false;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const checkHealth = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async () => {
      try {
        const res_0 = await fetch(`${BRIDGE_BASE}/scanners`, {
          signal: AbortSignal.timeout(5000)
        });
        if (res_0.ok) {
          const scanners = await res_0.json();
          setState(prev_1 => ({
            ...prev_1,
            scanners
          }));
        }
      } catch {}
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const fetchScanners = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      checkHealth().then(available => {
        if (available) {
          fetchScanners();
        }
      });
      healthTimerRef.current = setInterval(async () => {
        const available_0 = await checkHealth();
        if (available_0) {
          fetchScanners();
        }
      }, HEALTH_CHECK_INTERVAL);
      return () => {
        if (healthTimerRef.current) {
          clearInterval(healthTimerRef.current);
        }
      };
    };
    t5 = [checkHealth, fetchScanners];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = options => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      setState(_temp3);
      const ws = new WebSocket(BRIDGE_WS);
      wsRef.current = ws;
      ws.onopen = () => {
        ws.send(JSON.stringify({
          action: "scan",
          scannerId: options.scannerId,
          dpi: options.dpi ?? 300,
          colorMode: options.colorMode ?? "color",
          format: options.format ?? "pdf"
        }));
      };
      ws.onmessage = event => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          console.error("Failed to parse scanner bridge message");
          return;
        }
        bb105: switch (data.type) {
          case "scan_started":
            {
              setState(_temp4);
              break bb105;
            }
          case "scan_progress":
            {
              setState(prev_6 => ({
                ...prev_6,
                currentPage: data.page
              }));
              break bb105;
            }
          case "scan_complete":
            {
              setState(prev_5 => ({
                ...prev_5,
                scanState: "complete",
                scannedPages: data.pages
              }));
              ws.close();
              break bb105;
            }
          case "scan_error":
            {
              setState(prev_4 => ({
                ...prev_4,
                scanState: "error",
                error: data.message
              }));
              ws.close();
              break bb105;
            }
          case "error":
            {
              setState(prev_3 => ({
                ...prev_3,
                scanState: "error",
                error: data.message
              }));
              ws.close();
            }
        }
      };
      ws.onerror = () => {
        setState(_temp5);
      };
      ws.onclose = () => {
        wsRef.current = null;
      };
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const startScan = t6;
  let t7;
  if ($[7] !== state.scannedPages) {
    t7 = async filename => {
      const pages = state.scannedPages;
      if (pages.length === 0) {
        return null;
      }
      const page = pages[0];
      const res_1 = await fetch(page.dataUrl);
      const blob = await res_1.blob();
      const isPdf = page.dataUrl.startsWith("data:application/pdf");
      const ext = isPdf ? "pdf" : "png";
      const mimeType = isPdf ? "application/pdf" : "image/png";
      const name = filename ?? `scan-${Date.now()}.${ext}`;
      return new File([blob], name, {
        type: mimeType
      });
    };
    $[7] = state.scannedPages;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const scannedPagesToFile = t7;
  let t8;
  if ($[9] !== state.scannedPages) {
    t8 = async () => {
      const files = [];
      for (let i = 0; i < state.scannedPages.length; i++) {
        const page_0 = state.scannedPages[i];
        const res_2 = await fetch(page_0.dataUrl);
        const blob_0 = await res_2.blob();
        const isPdf_0 = page_0.dataUrl.startsWith("data:application/pdf");
        const ext_0 = isPdf_0 ? "pdf" : "png";
        const mimeType_0 = isPdf_0 ? "application/pdf" : "image/png";
        files.push(new File([blob_0], `scan-${Date.now()}-${i + 1}.${ext_0}`, {
          type: mimeType_0
        }));
      }
      return files;
    };
    $[9] = state.scannedPages;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const scannedPagesToFiles = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      setState(_temp6);
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const resetScan = t9;
  let t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    t11 = [];
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  useEffect(t10, t11);
  const t12 = state.scanState === "scanning" || state.scanState === "connecting";
  let t13;
  if ($[14] !== scannedPagesToFile || $[15] !== scannedPagesToFiles || $[16] !== state.currentPage || $[17] !== state.error || $[18] !== state.isAvailable || $[19] !== state.scanState || $[20] !== state.scannedPages || $[21] !== state.scanners || $[22] !== t12) {
    t13 = {
      isAvailable: state.isAvailable,
      scanners: state.scanners,
      scanState: state.scanState,
      scannedPages: state.scannedPages,
      currentPage: state.currentPage,
      error: state.error,
      isScanning: t12,
      startScan,
      scannedPagesToFile,
      scannedPagesToFiles,
      resetScan,
      refreshScanners: fetchScanners
    };
    $[14] = scannedPagesToFile;
    $[15] = scannedPagesToFiles;
    $[16] = state.currentPage;
    $[17] = state.error;
    $[18] = state.isAvailable;
    $[19] = state.scanState;
    $[20] = state.scannedPages;
    $[21] = state.scanners;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}
function _temp6(prev_9) {
  return {
    ...prev_9,
    scanState: "idle",
    scannedPages: [],
    currentPage: 0,
    error: null
  };
}
function _temp5(prev_8) {
  return {
    ...prev_8,
    scanState: "error",
    error: "Failed to connect to Scanner Bridge"
  };
}
function _temp4(prev_7) {
  return {
    ...prev_7,
    scanState: "scanning"
  };
}
function _temp3(prev_2) {
  return {
    ...prev_2,
    scanState: "connecting",
    scannedPages: [],
    currentPage: 0,
    error: null
  };
}
function _temp2(prev) {
  if (!prev.isAvailable) {
    return prev;
  }
  return {
    ...prev,
    isAvailable: false,
    scanners: []
  };
}
function _temp(prev_0) {
  if (prev_0.isAvailable) {
    return prev_0;
  }
  return {
    ...prev_0,
    isAvailable: true
  };
}